/**
 * NapDetailsResultModal - Displays NAP Details fetched from the S2S API,
 * replicating the appearance of the Converge mobile app's "Nap Details" screen.
 *
 * Props:
 *   data - API response data (nap_code, used_port, vacant, onu_connect, audit, etc.)
 *   onClose - close the modal
 *   onMergeSave - if provided (merge flow), auto-saves on mount when merge data found
 *   onRefresh - if provided, shows a Refresh button that force re-fetches from the external
 *               API; should return a Promise (used to drive the loading state)
 */
import { useState, useEffect, useRef } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import AddNapCommentModal from "./AddNapCommentModal";
import { parseNapIds, NAP_PORT_OPTIONS } from "../common/NapFieldsGroup";

export default function NapDetailsResultModal({ data, onClose, onMergeSave, onRefresh, onChanged }) {
  const { hasPerm } = useAuth();

  // Hold details in state so a self-refresh can update what's displayed.
  const [details, setDetails] = useState(data);
  useEffect(() => { setDetails(data); }, [data]);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [activeTab, setActiveTab] = useState("audited");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [comments, setComments] = useState([]);
  const [expandedPhoto, setExpandedPhoto] = useState(null); // blob URL
  const [showAddComment, setShowAddComment] = useState(false);
  const expandedPhotoRef = useRef(null);
  const autoSaveTriggered = useRef(false);

  const { nap_code, used_port, vacant, onu_connect, last_audit, is_scan_completed, audit, from_cache, fetched_at, _isMerge } = details || {};
  const recordId = details?.id ?? details?._recordId ?? null;
  const [manualFullNap, setManualFullNap] = useState(!!details?.manual_full_nap);
  useEffect(() => { setManualFullNap(!!details?.manual_full_nap); }, [details]);
  const canComment = hasPerm("nap_locator.edit");

  const closePhoto = () => {
    if (expandedPhotoRef.current) {
      URL.revokeObjectURL(expandedPhotoRef.current);
      expandedPhotoRef.current = null;
    }
    setExpandedPhoto(null);
  };

  const openPhoto = async (photoUrl) => {
    try {
      const res = await api.get(photoUrl, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      expandedPhotoRef.current = blobUrl;
      setExpandedPhoto(blobUrl);
    } catch {
      // photo may not exist on disk
    }
  };

  const formatFetchedAt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString();
  };

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const canRefresh = !!onRefresh || (!_isMerge && !!recordId);

  const handleRefresh = async () => {
    if (!canRefresh) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      if (onRefresh) {
        await onRefresh();
      } else {
        // Self-refresh: force a live re-fetch and update what's displayed.
        const { data: fresh } = await api.post(`/nap-locator/${recordId}/nap-details?force=true`);
        if (fresh?.found) {
          setDetails({ ...fresh, _recordId: recordId });
          onChanged?.();
        } else {
          setRefreshError(fresh?.detail || "No NAP details found on the external server.");
        }
      }
    } catch (err) {
      setRefreshError(err?.response?.data?.detail || err?.message || "Failed to refresh NAP details.");
    } finally {
      setRefreshing(false);
    }
  };

  const fetchComments = () => {
    if (!nap_code) return;
    api.get("/nap-locator/comments", { params: { nap_code } })
      .then((res) => {
        setComments(res.data?.comments || []);
        if (typeof res.data?.manual_full_nap === "boolean") setManualFullNap(res.data.manual_full_nap);
      })
      .catch(() => { /* non-fatal */ });
  };

  const fetchHistory = () => {
    if (!nap_code) return;
    setHistoryLoading(true);
    setHistoryError(null);
    api.get("/nap-locator/job-history", { params: { nap_code } })
      .then((res) => setHistory(res.data))
      .catch(() => setHistoryError("Failed to load NAP history."))
      .finally(() => setHistoryLoading(false));
  };

  // Load comments + completed-job history on open. Both are needed up-front: the
  // combined timeline and the Audited banners use them, and the Add-Comment max
  // available ports = total ports − completed jobs.
  useEffect(() => {
    fetchComments();
    fetchHistory();
  }, [nap_code]);

  const handleCommentSaved = (saved) => {
    setShowAddComment(false);
    if (saved && typeof saved.is_full_nap === "boolean") setManualFullNap(saved.is_full_nap);
    fetchComments();
    fetchHistory();
    setActiveTab("history");
    onChanged?.();
  };

  // Auto-save when merge data is available
  useEffect(() => {
    if (_isMerge && onMergeSave && !autoSaveTriggered.current) {
      autoSaveTriggered.current = true;
      setSaving(true);
      onMergeSave(data)
        .then(() => { setSaved(true); })
        .catch((err) => { setSaveError(err?.message || "Failed to save merge."); })
        .finally(() => { setSaving(false); });
    }
  }, [_isMerge, onMergeSave, data]);

  if (!details) return null;

  // Normalize port number to 1-8 range (S2S API returns P9-P16 for second NAP port)
  const normalizePortNo = (portNo) => {
    if (!portNo) return "—";
    const match = portNo.match(/^P(\d+)$/i);
    if (!match) return portNo;
    const num = parseInt(match[1], 10);
    const normalized = ((num - 1) % 8) + 1;
    return `P${normalized}`;
  };

  const sourceLabel = (source) => source === "ni" ? "NI" : "MIG";
  const sourceBadgeClass = (source) =>
    source === "ni"
      ? "bg-blue-100 text-blue-700"
      : "bg-purple-100 text-purple-700";

  // A comment card in the combined NAP timeline.
  const renderCommentCard = (c) => (
    <div
      key={`comment-${c.id}`}
      className={`rounded-lg border p-3 text-xs shadow-sm ${c.is_full_nap ? "border-red-200 bg-red-50" : "border-gray-100 bg-white"}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="inline-flex items-center gap-1.5 font-semibold text-gray-800">
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-200 text-gray-600">Comment</span>
          {c.created_by_name || "—"}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {c.is_full_nap && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-red-600 text-white">Full NAP</span>
          )}
          <span className="text-[10px] text-gray-400">{formatDateTime(c.created_at)}</span>
        </div>
      </div>
      <p className="text-gray-700 whitespace-pre-wrap break-words">{c.comment}</p>
      {c.available_ports != null && (
        <p className="mt-1 text-[11px] text-gray-600">
          Reported available ports: <span className="font-semibold text-gray-800">{c.available_ports}</span>
        </p>
      )}
      {c.photo_url && (
        <button
          onClick={() => openPhoto(c.photo_url)}
          className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-[10px] font-medium text-gray-600 hover:bg-gray-100 shadow-sm"
          title="View photo"
        >
          <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4-4 4 4 4-6 4 6M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
          </svg>
          View Photo
        </button>
      )}
    </div>
  );

  // A completed-job card in the combined NAP timeline.
  const renderJobCard = (item) => (
    <div
      key={`${item.source}-${item.id}`}
      className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-xs shadow-sm"
    >
      {/* Header row: subscriber + source badge */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="font-semibold text-gray-900 leading-snug">{item.subscriber || "—"}</p>
        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${sourceBadgeClass(item.source)}`}>
          {sourceLabel(item.source)}
        </span>
      </div>

      {/* Grid of fields */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-gray-600">
        <div>
          <span className="text-gray-400 block text-[10px]">Account No.</span>
          <span className="font-medium text-gray-800">{item.account_number || "—"}</span>
        </div>
        <div>
          <span className="text-gray-400 block text-[10px]">NAP ID</span>
          <span className="font-medium text-gray-800">{item.nap_id || "—"}</span>
        </div>
        <div>
          <span className="text-gray-400 block text-[10px]">NAP Port</span>
          <span className="font-medium text-gray-800">{item.nap_port || "—"}</span>
        </div>
        <div>
          <span className="text-gray-400 block text-[10px]">Available Ports</span>
          <span className="font-medium text-gray-800">{item.vacant_ports ?? "—"}</span>
        </div>
        <div>
          <span className="text-gray-400 block text-[10px]">Modem Serial No.</span>
          <span className="font-medium text-gray-800 font-mono">{item.serial_number || "—"}</span>
        </div>
        <div>
          <span className="text-gray-400 block text-[10px]">Team</span>
          <span className="font-medium text-gray-800">{item.team_number || "—"}</span>
        </div>
        <div>
          <span className="text-gray-400 block text-[10px]">Personnel</span>
          <span className="font-medium text-gray-800">{item.personnel || "—"}</span>
        </div>
      </div>

      {/* Photo + date row */}
      <div className="flex items-end justify-between mt-2 pt-2 border-t border-gray-200">
        <div>
          <span className="text-gray-400 block text-[10px]">Date Completed</span>
          <span className="text-gray-700">{formatDateTime(item.completed_at)}</span>
        </div>
        {item.photo_url ? (
          <button
            onClick={() => openPhoto(item.photo_url)}
            className="flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-[10px] font-medium text-gray-600 hover:bg-gray-100 shadow-sm"
            title="View NAP photo"
          >
            <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4-4 4 4 4-6 4 6M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
            </svg>
            View Photo
          </button>
        ) : (
          <span className="text-[10px] text-gray-300 italic">No photo</span>
        )}
      </div>
    </div>
  );

  // Merge comments + completed jobs into one timeline, newest first.
  const timelineItems = [
    ...comments.map((c) => ({ kind: "comment", ts: c.created_at, data: c })),
    ...history.map((h) => ({ kind: "job", ts: h.completed_at, data: h })),
  ].sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));

  // "Actual available ports" reports come from two sources: NAP comments (explicit
  // value, or Full NAP ⇒ 0) and completed NI/Migration jobs (the Vacant Ports the
  // tech entered on completion). The most recent of these drives the Audited banner.
  const portReports = [
    ...comments
      .filter((c) => c.available_ports != null || c.is_full_nap)
      .map((c) => ({
        ts: c.created_at,
        value: c.available_ports != null ? c.available_ports : 0,
        by: c.created_by_name || "—",
        kind: "comment",
      })),
    ...history
      .filter((h) => h.vacant_ports != null)
      .map((h) => ({
        ts: h.completed_at,
        value: h.vacant_ports,
        by: h.personnel || h.team_number || (h.source === "ni" ? "NI completion" : "Migration completion"),
        kind: "job",
      })),
  ].sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));

  const latestPortReport = portReports[0] || null;
  const reportedAvailablePorts = latestPortReport ? latestPortReport.value : null;
  const reportAttribution = latestPortReport
    ? `${latestPortReport.kind === "job" ? "From a completed job by" : "Reported by"} ${latestPortReport.by} on ${formatDateTime(latestPortReport.ts)}.`
    : "";

  // Total ports for this NAP code. Prefer the stored value; otherwise derive from
  // the NAP code structure: each NP designator (e.g. NP1, NP2) has 8 ports.
  const derivedTotalPorts = parseNapIds(nap_code).length * NAP_PORT_OPTIONS.length || null;
  const totalPorts = (details?.no_of_ports ?? null) || derivedTotalPorts;
  // Max available ports a comment may report = total ports − completed jobs on this NAP.
  const maxAvailablePorts = totalPorts != null ? Math.max(0, totalPorts - history.length) : null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
      {/* Full-screen loading overlay while refreshing */}
      {refreshing && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/20">
          <div className="relative flex items-center justify-center w-20 h-20">
            <svg
              className="absolute inset-0 w-full h-full animate-spin"
              style={{ animationDuration: "1.5s" }}
              viewBox="0 0 100 100"
              fill="none"
            >
              <polygon
                points="50,2 93,25 93,75 50,98 7,75 7,25"
                stroke="#2563eb"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="12 6"
              />
            </svg>
            <img src="/best-icon.png" alt="Loading..." className="h-12 w-12" />
          </div>
        </div>
      )}

      {/* Expanded photo lightbox */}
      {expandedPhoto && (
        <div
          className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/80"
          onClick={closePhoto}
        >
          <img
            src={expandedPhoto}
            alt="NAP Photo"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-2xl"
          />
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white text-xl hover:bg-white/40"
            onClick={closePhoto}
          >
            ×
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="bg-gray-100 rounded-t-lg px-5 py-4 border-b">
          <h2 className="text-base font-bold text-gray-800">Nap Details</h2>
        </div>

        {/* NAP Info Section */}
        <div className="px-5 py-3 border-b bg-gray-50">
          <p className="text-sm font-semibold text-gray-700">NAP: {nap_code}</p>
          <p className="text-xs text-gray-500">Last Audited: {last_audit || "—"}</p>
          <div className="mt-2">
            <p className="text-xs text-gray-500 font-medium">NIMS Record:</p>
            <p className="text-sm text-gray-800">Port Used: <span className="font-semibold">{used_port}</span></p>
            <p className="text-sm text-gray-800">Unused Ports: <span className="font-semibold">{vacant}</span></p>
            {onu_connect > 0 && (
              <p className="text-sm text-gray-800">ONU Connect: <span className="font-semibold">{onu_connect}</span></p>
            )}
          </div>
        </div>

        {/* Tab navigation */}
        <div className="flex border-b bg-white px-5 gap-1">
          <button
            onClick={() => setActiveTab("audited")}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 -mb-px ${
              activeTab === "audited"
                ? "border-gray-800 text-gray-800"
                : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            Audited
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 -mb-px ${
              activeTab === "history"
                ? "border-gray-800 text-gray-800"
                : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            NAP History
          </button>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">
          {/* AUDITED tab */}
          {activeTab === "audited" && (
            <div className="px-5 py-2">
              {manualFullNap ? (
                <div className="mb-2 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                  <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <span>
                    <span className="font-bold">Manually marked as FULL NAP — 0 available port(s).</span>{" "}
                    System shows {vacant ?? "—"} vacant port(s).
                    {reportAttribution && ` ${reportAttribution}`}
                  </span>
                </div>
              ) : reportedAvailablePorts != null && (
                <div className="mb-2 flex items-start gap-2 rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>
                    <span className="font-bold">Actual available ports: {reportedAvailablePorts}.</span>{" "}
                    System shows {vacant ?? "—"}.
                    {reportAttribution && ` ${reportAttribution}`}
                  </span>
                </div>
              )}
              {audit && audit.length > 0 ? (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-gray-500">
                      <th className="py-1.5 text-left font-medium">Nap Port</th>
                      <th className="py-1.5 text-left font-medium">Port No</th>
                      <th className="py-1.5 text-left font-medium">Serial Number</th>
                      <th className="py-1.5 text-left font-medium">Package</th>
                      <th className="py-1.5 text-left font-medium">Activity Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((entry, idx) => (
                      <tr key={idx} className={`border-b border-gray-100 ${idx % 2 === 0 ? "" : "bg-gray-50"}`}>
                        <td className="py-1.5 text-gray-700">{entry.NapPorts || "—"}</td>
                        <td className="py-1.5 text-gray-700">{normalizePortNo(entry.PortNo || entry.Port)}</td>
                        <td className="py-1.5 text-gray-700 font-mono text-[10px]">{entry.Sn || ""}</td>
                        <td className="py-1.5 text-gray-700">{entry.Package || ""}</td>
                        <td className="py-1.5 text-gray-700">{entry.ActivityStatus || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No audit entries</p>
              )}
            </div>
          )}

          {/* NAP HISTORY tab */}
          {activeTab === "history" && (
            <div className="px-4 py-3">
              {historyError && (
                <p className="text-sm text-red-500 text-center py-2">{historyError}</p>
              )}
              {historyLoading && timelineItems.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">Loading history...</p>
              ) : timelineItems.length === 0 ? (
                <div className="flex flex-col items-center py-8 text-gray-400">
                  <svg className="w-10 h-10 mb-2 text-gray-200" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
                  </svg>
                  <p className="text-xs">No history recorded for this NAP.</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {historyLoading && (
                    <p className="text-[11px] text-gray-400 text-center">Loading completed jobs…</p>
                  )}
                  {timelineItems.map((it) =>
                    it.kind === "comment" ? renderCommentCard(it.data) : renderJobCard(it.data)
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex flex-col gap-2">
          {_isMerge && saving && (
            <p className="text-sm text-blue-600 font-medium text-center">Saving merge data & generating QR...</p>
          )}
          {_isMerge && saved && (
            <p className="text-sm text-green-600 font-medium text-center">Merged successfully! QR generated.</p>
          )}
          {_isMerge && saveError && (
            <p className="text-sm text-red-600 font-medium text-center">{saveError}</p>
          )}
          {fetched_at && (
            <p className="text-[11px] text-gray-400 text-center">
              {from_cache ? "Loaded from server" : "Updated"} · {formatFetchedAt(fetched_at)}
            </p>
          )}
          {refreshError && (
            <p className="text-sm text-red-600 font-medium text-center">{refreshError}</p>
          )}
          <div className="flex items-center justify-between gap-2">
            {/* Bottom-left: Add Comment */}
            <div>
              {canComment && recordId && !_isMerge && (
                <button
                  onClick={() => setShowAddComment(true)}
                  disabled={saving || refreshing}
                  className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
                  title="Add a comment to this NAP's history"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 20l1.3-3.9A7.96 7.96 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Add Comment
                </button>
              )}
            </div>
            {/* Bottom-right: OK + Refresh */}
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                disabled={saving || refreshing}
                className="px-8 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-full shadow transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : "OK"}
              </button>
              {canRefresh && (
                <button
                  onClick={handleRefresh}
                  disabled={refreshing || saving}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-full shadow transition-colors disabled:opacity-50"
                  title="Re-fetch the latest data from the external server"
                >
                  {refreshing ? "Refreshing..." : "Refresh"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Comment popup */}
      {showAddComment && recordId && (
        <AddNapCommentModal
          recordId={recordId}
          napCode={nap_code}
          initialFullNap={manualFullNap}
          maxAvailablePorts={maxAvailablePorts}
          onClose={() => setShowAddComment(false)}
          onSaved={handleCommentSaved}
        />
      )}
    </div>
  );
}

